import React, { useState, useEffect, useCallback, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './InspectorDashboardTab.css';
import { API_BASE_URL, getMediaBaseUrl } from '../config';
import { fetchWithRetry } from '../services/inspectorService';
import { storageService } from '../services/storageService';
import { useAuth } from '../context/AuthContext';
import { InspectorLoadingOverlay } from '../components/InspectorLoadingOverlay';
import ImageViewerModal from '../components/ImageViewerModal';
import { formatInspectorError } from '../utils/errorUtils';
import { useOfflineSync } from '../hooks/useOfflineSync';

// Hook para manejar retroceso de historial en modales
const useBackHandler = (active, onBack, name) => {
  const isManual = useRef(false);

  useEffect(() => {
    if (!active) return;

    isManual.current = false;

    window.history.pushState({ modalName: name }, '');

    const handlePopState = (e) => {
      if (isManual.current) {
        isManual.current = false;
        return;
      }

      const currentState = e.state || {};
      if (currentState.modalName !== name) {
        onBack();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [active, onBack, name]);

  const triggerManualBack = useCallback((...args) => {
    if (active) {
      isManual.current = true;
      window.history.back();
    }
    onBack(...args);
  }, [active, onBack]);

  return triggerManualBack;
};

const DEFAULT_COORDINATES = { latitude: 23.6345, longitude: -102.5528 };

function getAuthHeaders() {
  const token = localStorage.getItem('control_campo_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

function getAllowedWeekIds() {
  const getISOWeekYear = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const week1 = new Date(d.getFullYear(), 0, 4);
    const weekNum = 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    return [d.getFullYear(), weekNum];
  };
  const now = new Date();
  const results = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() - (i * 7));
    const [y, w] = getISOWeekYear(d);
    results.push(`${y}-W${String(w).padStart(2, '0')}`);
  }
  return results;
}

// ─────────────────────────────────────────────
// Fotos del técnico (miniaturas)
// ─────────────────────────────────────────────
const TechPhotos = ({ segment, onExpandImage }) => {
  const photos = [
    segment?.photo1_path,
    segment?.photo2_path,
  ].filter(Boolean);
  if (photos.length === 0) return null;

  const baseUrl = getMediaBaseUrl();

  return (
    <div className="tech-photos-wrap">
      <p className="tech-photos-title">📸 Fotos del técnico</p>
      <div className="tech-photos-row">
        {photos.map((src, i) => {
          const imgUrl = `${baseUrl}/static/uploads/${src}`;
          return (
            <button
              key={i}
              type="button"
              className="tech-photo-link"
              onClick={() => onExpandImage && onExpandImage(imgUrl)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              <img src={imgUrl} loading="lazy" decoding="async" alt={`Foto técnico ${i + 1}`} className="tech-photo-img" />
              <span className="tech-photo-zoom">🔍</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const STANDARD_PI_ACTIVITIES = [
  'Acomodo y tensado en altura',
  'Organización de acometidas',
  'Cerrado de caja de 2N',
  'Retiro de acometida',
  'Poda de árboles',
];

const parseTechActivities = (rawActivities) => {
  if (!rawActivities) return [];
  if (Array.isArray(rawActivities)) return rawActivities.map(a => String(a).trim()).filter(Boolean);
  if (typeof rawActivities === 'string') {
    try {
      const parsed = JSON.parse(rawActivities);
      if (Array.isArray(parsed)) return parsed.map(a => String(a).trim()).filter(Boolean);
      if (parsed) return [String(parsed).trim()];
    } catch {
      return rawActivities.split(',').map(s => s.trim()).filter(Boolean);
    }
  }
  return [];
};

// ─────────────────────────────────────────────
// Modal validación de segmento
// ─────────────────────────────────────────────
const SegmentValidationModal = ({ segment, report, onSuccess, onCancel, onOfflineQueued }) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [expandedImage, setExpandedImage] = useState(null);
  const [maintenanceDone, setMaintenanceDone] = useState(null);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validatedActivities, setValidatedActivities] = useState({});
  const [currentPosition, setCurrentPosition] = useState(null);
  const [compressing, setCompressing] = useState(false);

  const techActivities = React.useMemo(() => parseTechActivities(segment?.activities), [segment?.activities]);

  const otherActivities = React.useMemo(() => {
    return STANDARD_PI_ACTIVITIES.filter(act => !techActivities.includes(act));
  }, [techActivities]);

  const allAvailableActivities = React.useMemo(() => {
    return Array.from(new Set([...techActivities, ...STANDARD_PI_ACTIVITIES]));
  }, [techActivities]);

  useEffect(() => {
    const initial = {};
    allAvailableActivities.forEach(act => {
      initial[act] = false; // Inicia desmarcado por defecto
    });
    setValidatedActivities(initial);
  }, [allAvailableActivities]);

  useEffect(() => {
    // Obtener la geolocalización al abrir el modal para tenerla lista para la marca de agua
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setCurrentPosition({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          });
        },
        (err) => {
          console.error("Error obteniendo ubicación para marca de agua:", err);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
  }, []);

  useEffect(() => {
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previews]);

  // 🎯 Función para comprimir imagen con límite de peso de 750 KB (0.7 MB)
  const compressImage = (file, maxSizeMB = 0.7) => {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        // Liberar el Object URL inmediatamente para liberar RAM
        URL.revokeObjectURL(objectUrl);

        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Mantener aspecto reduciendo si es muy grande
        const maxWidth = 1200;
        const maxHeight = 1200;
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // --- ESTAMPAR INFORMACIÓN DE GEOLOCALIZACIÓN Y TÉCNICO (OPENCAM STYLE) ---
        const bannerHeight = Math.round(height * 0.16); // 16% del alto
        ctx.fillStyle = 'rgba(0, 0, 0, 0.65)';
        ctx.fillRect(0, height - bannerHeight, width, bannerHeight);

        // Línea azul de acento superior en el banner
        ctx.fillStyle = '#2563eb';
        ctx.fillRect(0, height - bannerHeight, width, Math.max(3, Math.round(height * 0.005)));

        // Ajustes de texto
        ctx.fillStyle = '#ffffff';
        ctx.font = `bold ${Math.round(bannerHeight * 0.13)}px system-ui, -apple-system, sans-serif`;
        ctx.textBaseline = 'top';

        const nameText = `Inspector: ${user?.full_name || 'N/A'} (${user?.username || 'N/A'})`;
        const regionText = `Región: ${user?.region || 'N/A'} | Distrito: ${user?.distrito || user?.district || 'N/A'}`;
        const dateText = `Fecha/Hora: ${new Date().toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}`;
        const gpsText = `Coordenadas GPS: ${currentPosition?.latitude?.toFixed(6) || 'N/A'}, ${currentPosition?.longitude?.toFixed(6) || 'N/A'}`;

        const startX = Math.round(width * 0.04); // 4% de margen izquierdo
        const lineSpacing = Math.round(bannerHeight * 0.21);
        const startY = height - bannerHeight + Math.round(bannerHeight * 0.10);

        ctx.fillText(nameText, startX, startY);
        ctx.fillText(regionText, startX, startY + lineSpacing);
        ctx.fillText(dateText, startX, startY + lineSpacing * 2);
        ctx.fillText(gpsText, startX, startY + lineSpacing * 3);

        // Compresión eficiente en máximo 2 pasos para ahorrar CPU y RAM
        const quality = 0.70; // 0.70 es excelente calidad visual y tamaño muy optimizado (~150KB)
        const cleanupCanvas = () => {
          canvas.width = 0;
          canvas.height = 0;
          img.onload = null;
          img.onerror = null;
          img.src = '';
        };
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              cleanupCanvas();
              reject(new Error('Error al generar blob de la imagen'));
              return;
            }
            const sizeMB = blob.size / (1024 * 1024);
            if (sizeMB > maxSizeMB) {
              // Si aún supera el límite (muy raro para 1200px a 0.70), intentamos con 0.50
              canvas.toBlob(
                (secondBlob) => {
                  cleanupCanvas();
                  if (!secondBlob) {
                    resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                    return;
                  }
                  console.log(`📦 [PI] Compresión (Paso 2): ${(file.size / 1024).toFixed(1)} KB -> ${(secondBlob.size / 1024).toFixed(1)} KB`);
                  resolve(new File([secondBlob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
                },
                'image/jpeg',
                0.50
              );
            } else {
              cleanupCanvas();
              console.log(`📦 [PI] Compresión (Paso 1): ${(file.size / 1024).toFixed(1)} KB -> ${(blob.size / 1024).toFixed(1)} KB`);
              resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Error al procesar la imagen'));
      };
      img.src = objectUrl;
    });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 1 - photos.length;
    const toAdd = files.slice(0, remaining);

    const errors = [];
    const validToAdd = [];
    const MAX_PRE_COMPRESS_SIZE = 15 * 1024 * 1024; // 15MB

    for (const f of toAdd) {
      if (!f.type.startsWith('image/')) {
        errors.push(`❌ ${f.name} no es una imagen válida`);
        continue;
      }
      if (f.size > MAX_PRE_COMPRESS_SIZE) {
        errors.push(`❌ ${f.name} supera el límite de 15MB`);
        continue;
      }
      validToAdd.push(f);
    }

    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }

    if (toAdd.length < files.length) {
      setError(`Solo puedes agregar ${remaining} foto más`);
    } else {
      setError('');
    }

    setCompressing(true);
    // Comprimir cada imagen válida antes de guardarla
    Promise.all(validToAdd.map(f => compressImage(f).catch(err => {
      console.error(err);
      return f; // fallback al original en caso de error
    }))).then(compressedFiles => {
      setPhotos(prev => [...prev, ...compressedFiles]);
      setPreviews(prev => [...prev, ...compressedFiles.map(f => URL.createObjectURL(f))]);
    }).finally(() => {
      setCompressing(false);
      e.target.value = '';
    });
  };

  const removePhoto = (index) => {
    URL.revokeObjectURL(previews[index]);
    setPhotos(photos.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const getGeolocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: String(DEFAULT_COORDINATES.latitude), longitude: String(DEFAULT_COORDINATES.longitude) });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: String(pos.coords.latitude.toFixed(6)), longitude: String(pos.coords.longitude.toFixed(6)) }),
        () => resolve({ latitude: String(DEFAULT_COORDINATES.latitude), longitude: String(DEFAULT_COORDINATES.longitude) }),
        // maximumAge 2 min: si hay lectura GPS reciente la usa instantáneamente
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 120000 }
      );
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (maintenanceDone === null) { setError('Indica si se realizó mantenimiento'); return; }
    if (!comments.trim()) { setError('Los comentarios son obligatorios'); return; }
    if (maintenanceDone === true) {
      const hasAnyChecked = Object.values(validatedActivities).some(v => v === true);
      if (!hasAnyChecked) {
        setError('Debes marcar al menos una actividad realizada para validar como SÍ');
        return;
      }
    }
    if (photos.length === 0) {
      setError(maintenanceDone === true
        ? 'Se requiere la foto panorámica que demuestre los trabajos realizados'
        : 'Se requiere al menos 1 foto cuando NO se realizó mantenimiento');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Usar la ubicación ya obtenida en segundo plano si existe para evitar retraso de GPS en submit
      const coords = currentPosition
        ? { latitude: String(currentPosition.latitude), longitude: String(currentPosition.longitude) }
        : await getGeolocation();
      const formData = new FormData();
      formData.append('maintenance_done', String(maintenanceDone));
      formData.append('inspector_comments', comments);
      formData.append('inspector_latitude', coords.latitude);
      formData.append('inspector_longitude', coords.longitude);

      if (maintenanceDone === true) {
        formData.append('validated_activities', JSON.stringify(validatedActivities));
      }

      photos.forEach(photo => formData.append('inspector_photos', photo));

      // Usar fetchWithRetry en lugar de fetch directo para tolerar señal inestable
      try {
        const res = await fetchWithRetry(
          `${API_BASE_URL}/inspector/routes/${report.id}/segments/${segment.id}/validate`,
          { method: 'POST', headers: getAuthHeaders(), body: formData }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Error al guardar validación');
        }
        onSuccess();
      } catch (networkErr) {
        const isOffline = !navigator.onLine ||
          networkErr.name === 'TypeError' ||
          networkErr.message?.toLowerCase().includes('network') ||
          networkErr.message?.toLowerCase().includes('failed to fetch') ||
          networkErr.message?.toLowerCase().includes('conexion') ||
          networkErr.message?.toLowerCase().includes('sin respuesta');

        if (isOffline) {
          console.warn('📶 Sin conexión al validar segmento PI, guardando en cola offline...');
          await storageService.queueInspectorAction({
            type: 'validate_pi_segment',
            label: `Validación Segmento PI #${segment.id}`,
            endpoint: `${API_BASE_URL}/inspector/routes/${report.id}/segments/${segment.id}/validate`,
            method: 'POST',
            fields: {
              maintenance_done: String(maintenanceDone),
              inspector_comments: comments,
              inspector_latitude: coords.latitude,
              inspector_longitude: coords.longitude,
              ...(maintenanceDone === true ? { validated_activities: JSON.stringify(validatedActivities) } : {})
            },
            photos: photos,
            photoFieldName: 'inspector_photos'
          });
          // Notificar al padre con Toast en lugar de alert() nativo bloqueante
          if (onOfflineQueued) onOfflineQueued(`📴 Sin señal — Validación del segmento #${segment.id} guardada. Se enviará automáticamente cuando recuperes señal.`);
          onSuccess();
        } else {
          throw networkErr;
        }
      }
    } catch (err) {
      setError(formatInspectorError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="val-overlay">
      <div className="val-sheet">
        <div className="val-header">
          <div>
            <h2 className="val-title">Validar segmento</h2>
            <p className="val-sub">#{segment?.id} · {segment?.type === 'realizable' ? 'Auditable' : 'No auditable'}</p>
          </div>
          <button className="val-close" onClick={onCancel} disabled={loading}>✕</button>
        </div>

        {segment?.comments && <p className="val-seg-comment">💬 {segment.comments}</p>}
        <TechPhotos segment={segment} onExpandImage={setExpandedImage} />
        {error && <div className="val-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="yn-section">
            <p className="yn-question">¿Se realizó el mantenimiento?</p>
            <div className="yn-row">
              <button type="button"
                className={`yn-btn yn-si ${maintenanceDone === true ? 'active' : ''}`}
                onClick={() => setMaintenanceDone(true)} disabled={loading}>
                ✓ SÍ
              </button>
              <button type="button"
                className={`yn-btn yn-no ${maintenanceDone === false ? 'active' : ''}`}
                onClick={() => setMaintenanceDone(false)} disabled={loading}>
                ✕ NO
              </button>
            </div>
          </div>

          {maintenanceDone === true && (
            <div className="activities-validation-section" style={{
              marginTop: '15px',
              padding: '16px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              marginBottom: '15px'
            }}>
              <div style={{ marginBottom: '14px' }}>
                <p style={{ fontWeight: '700', margin: '0 0 4px 0', fontSize: '14.5px', color: '#1e293b' }}>
                  📋 Actividades de mantenimiento del punto:
                </p>
                <p style={{ margin: 0, fontSize: '12.5px', color: '#64748b' }}>
                  Marca únicamente las actividades que <b>SÍ se realizaron correctamente</b> en campo.
                </p>
              </div>

              {/* 1. Sección: Actividades reportadas por el técnico */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    👤 Reportadas por el técnico
                  </span>
                  {techActivities.length > 0 && (
                    <span style={{ fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '10px', fontWeight: '600' }}>
                      {techActivities.length} {techActivities.length === 1 ? 'actividad' : 'actividades'}
                    </span>
                  )}
                </div>

                {techActivities.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {techActivities.map(activity => {
                      const isChecked = validatedActivities[activity] ?? false;
                      return (
                        <label
                          key={activity}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: isChecked ? '#eff6ff' : '#ffffff',
                            border: isChecked ? '1.5px solid #3b82f6' : '1px solid #cbd5e1',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const val = e.target.checked;
                                setValidatedActivities(prev => ({
                                  ...prev,
                                  [activity]: val
                                }));
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '13.5px', fontWeight: isChecked ? '600' : '500', color: isChecked ? '#1e40af' : '#334155' }}>
                              {activity}
                            </span>
                          </div>
                          <span style={{
                            fontSize: '11px',
                            background: '#e0f2fe',
                            color: '#0369a1',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            🏷️ Marcado por técnico
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: '8px', fontSize: '12.5px', color: '#64748b', fontStyle: 'italic' }}>
                    ℹ️ El técnico no marcó actividades específicas en este punto
                  </div>
                )}
              </div>

              {/* 2. Sección: Otras actividades de mantenimiento disponibles */}
              {otherActivities.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      ➕ Otras actividades disponibles
                    </span>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                      (No reportadas por el técnico)
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {otherActivities.map(activity => {
                      const isChecked = validatedActivities[activity] ?? false;
                      return (
                        <label
                          key={activity}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 12px',
                            background: isChecked ? '#f0fdf4' : '#ffffff',
                            border: isChecked ? '1.5px solid #22c55e' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const val = e.target.checked;
                                setValidatedActivities(prev => ({
                                  ...prev,
                                  [activity]: val
                                }));
                              }}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '13.5px', fontWeight: isChecked ? '600' : '400', color: isChecked ? '#15803d' : '#64748b' }}>
                              {activity}
                            </span>
                          </div>
                          <span style={{
                            fontSize: '11px',
                            background: isChecked ? '#dcfce7' : '#f1f5f9',
                            color: isChecked ? '#166534' : '#94a3b8',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}>
                            {isChecked ? '✓ Agregado por inspector' : 'No marcado por técnico'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {maintenanceDone !== null && (
            <div className="photos-section">
              <p className="photos-label">
                {maintenanceDone === true
                  ? "📸 Foto panorámica que demuestre los trabajos realizados"
                  : "📸 Foto requerida (1)"}
                {" "}<span className="photos-count">{photos.length}/1</span>
              </p>
              <div className="photos-grid">
                {previews.map((src, i) => (
                  <div key={i} className="photo-thumb" style={{ cursor: 'pointer' }}>
                    <img src={src} alt={`Foto ${i + 1}`} onClick={() => setExpandedImage(src)} />
                    <button type="button" className="photo-del" onClick={(e) => { e.stopPropagation(); removePhoto(i); }} disabled={loading}>✕</button>
                  </div>
                ))}
                {photos.length < 1 && !compressing && (
                  <div className="photo-add-options">
                    <label className="photo-add photo-add-camera">
                      <span className="photo-add-icon">📷</span>
                      <span className="photo-add-text">Cámara</span>
                      <input type="file" accept="image/*" capture="environment"
                        onChange={handlePhotoUpload} disabled={loading || compressing} style={{ display: 'none' }} />
                    </label>
                    <label className="photo-add photo-add-gallery">
                      <span className="photo-add-icon">🖼️</span>
                      <span className="photo-add-text">Galería</span>
                      <input type="file" accept="image/*"
                        onChange={handlePhotoUpload} disabled={loading || compressing} style={{ display: 'none' }} />
                    </label>
                  </div>
                )}
                {compressing && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '16px', background: '#f0f7ff', borderRadius: '12px', border: '2px dashed #007bff', gap: '8px', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: '24px' }}>⏳</span>
                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#007bff' }}>Procesando y comprimiendo foto...</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="form-field">
            <label className="form-lbl">Comentarios</label>
            <textarea className="form-ta" rows={3}
              value={comments} onChange={e => setComments(e.target.value)}
              placeholder="Observaciones del inspector…" disabled={loading} />
          </div>

          <div className="val-actions">
            <button type="submit"
              disabled={loading || maintenanceDone === null || photos.length === 0 || !comments.trim()}
              className="btn-save">
              {loading ? 'Guardando…' : '💾 Guardar validación'}
            </button>
            <button type="button" onClick={onCancel} disabled={loading} className="btn-cancel-val">
              Cancelar
            </button>
          </div>
        </form>

        {/* Modal de visualización de imagen ampliada con soporte de botón Atrás del celular y tache X */}
        <ImageViewerModal src={expandedImage} onClose={() => setExpandedImage(null)} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Modal detalles del recorrido
// ─────────────────────────────────────────────
const ReportDetailsModal = ({ report, onClose, onOfflineQueued }) => {
  const [view, setView] = useState('list'); // 'list' | 'map' | 'validate'
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segments, setSegments] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const gpsMarkerRef = useRef(null);
  const gpsWatchRef = useRef(null);

  const destroyMap = () => {
    if (gpsWatchRef.current) { navigator.geolocation.clearWatch(gpsWatchRef.current); gpsWatchRef.current = null; }
    if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; }
    gpsMarkerRef.current = null;
  };

  const handleCloseMap = useBackHandler(view === 'map', () => {
    destroyMap();
    setView('list');
  }, 'reportMap');

  const handleCancelValidation = useBackHandler(view === 'validate', () => {
    setView('list');
    setSelectedSegment(null);
  }, 'segmentValidation');

  useEffect(() => {
    return () => {
      destroyMap();
    };
  }, []);

  const loadSegments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/inspector/routes/${report.id}/pending-segments`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Error cargando segmentos');
      const data = await res.json();
      const list = data.pending_segments || [];
      setSegments(list);
      localStorage.setItem(`inspector_cached_segments_${report.id}`, JSON.stringify(list));

      // Precargar fotos del técnico en la caché del navegador para verlas sin conexión
      try {
        const baseUrl = getMediaBaseUrl();
        list.forEach(s => {
          [s.photo1_path, s.photo2_path].filter(Boolean).forEach(p => {
            const img = new Image();
            img.src = `${baseUrl}/static/uploads/${p}`;
          });
        });
      } catch (e) {
        console.warn('Error precargando fotos del técnico:', e);
      }
    } catch (err) {
      console.warn('⚠️ Sin conexión al cargar segmentos, cargando desde caché local...');
      const cached = localStorage.getItem(`inspector_cached_segments_${report.id}`);
      if (cached) {
        try {
          setSegments(JSON.parse(cached) || []);
        } catch (e) {}
      } else {
        const isNetworkErr = !navigator.onLine || err.name === 'TypeError' || err.message?.toLowerCase().includes('fetch');
        if (!isNetworkErr) {
          setError(formatInspectorError(err));
        }
      }
    } finally {
      setLoading(false);
    }
  }, [report.id]);

  const loadMapData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/inspector/routes/${report.id}/map-data`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Error cargando mapa');
      const data = await res.json();
      setMapData(data);
      localStorage.setItem(`inspector_cached_map_${report.id}`, JSON.stringify(data));
      setView('map');
    } catch (err) {
      console.warn('⚠️ Sin conexión al cargar mapa, cargando desde caché local...');
      const cached = localStorage.getItem(`inspector_cached_map_${report.id}`);
      if (cached) {
        try {
          setMapData(JSON.parse(cached));
          setView('map');
        } catch (e) {
          setError('No hay datos de mapa guardados para modo offline');
        }
      } else {
        const isNetworkErr = !navigator.onLine || err.name === 'TypeError' || err.message?.toLowerCase().includes('fetch');
        if (!isNetworkErr) {
          setError(formatInspectorError(err));
        }
      }
    } finally {
      setLoading(false);
    }
  }, [report.id]);

  useEffect(() => { loadSegments(); }, [loadSegments]);

  useEffect(() => {
    if (view === 'map' && mapData && mapRef.current && !leafletMapRef.current) {
      initializeMap();
    }
  }, [view, mapData]); // eslint-disable-line

  const initializeMap = () => {
    if (!mapData || !mapRef.current) return;
    const rawPoints = (mapData.map_points || []).filter(
      p => !isNaN(parseFloat(p.lat)) && !isNaN(parseFloat(p.lng))
    );

    const center = rawPoints.length > 0
      ? [parseFloat(rawPoints[0].lat), parseFloat(rawPoints[0].lng)]
      : [DEFAULT_COORDINATES.latitude, DEFAULT_COORDINATES.longitude];

    const map = L.map(mapRef.current).setView(center, 14);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19
    }).addTo(map);
    setTimeout(() => { if (map) map.invalidateSize(); }, 250);

    const bounds = L.latLngBounds();
    // Ordenar los puntos por ID segmento (de menor a mayor) para ver la trayectoria real
    const sorted = [...rawPoints].sort((a, b) => {
      const idA = typeof a.id === 'string' ? parseInt(a.id.replace(/\D/g, ''), 10) : a.id;
      const idB = typeof b.id === 'string' ? parseInt(b.id.replace(/\D/g, ''), 10) : b.id;
      return (idA || 0) - (idB || 0);
    });

    const routeCoords = sorted.map(p => [parseFloat(p.lat), parseFloat(p.lng)]);
    if (routeCoords.length > 1) {
      L.polyline(routeCoords, { color: '#3b82f6', weight: 4, opacity: 0.8, dashArray: '8, 8' }).addTo(map);
    }

    // 1. Agrupar puntos cercanos / sobrepuestos (agrupador real por distancia)
    const clusters = [];

    // Distancia simple en metros
    const getDistance = (lat1, lon1, lat2, lon2) => {
      const R = 6371e3;
      const x = (lon2 - lon1) * Math.cos((lat1 + lat2) / 2 * Math.PI / 180);
      const y = (lat2 - lat1);
      return Math.sqrt(x * x + y * y) * (Math.PI / 180) * R;
    };

    rawPoints.forEach(p => {
      const lat = parseFloat(p.lat);
      const lng = parseFloat(p.lng);
      bounds.extend([lat, lng]);

      let foundCluster = null;
      for (const cl of clusters) {
        if (getDistance(lat, lng, cl.lat, cl.lng) < 30) { // Radio de 30 metros para absorber
          foundCluster = cl;
          break;
        }
      }

      if (foundCluster) {
        foundCluster.points.push(p);
      } else {
        clusters.push({ lat, lng, points: [p] });
      }
    });

    // Estado del spiderfy "on-click"
    let activeSpiders = [];
    const clearSpiders = () => {
      activeSpiders.forEach(layer => {
        if (layer && map.hasLayer(layer)) map.removeLayer(layer);
      });
      activeSpiders = [];
    };

    // Si se toca en el mapa vacío, cerramos nodos extendidos
    map.on('click', clearSpiders);

    // 2. Función para renderizar un punto (segmento o no realizable)
    const renderOriginalPoint = (pt, displayLat, displayLng, isSpiderfied, clusterCenter) => {
      const isSegment = pt.type === 'segment';
      const isPending = isSegment && segments.some(s => s.id === pt.id);

      let fillColor = '#dc2626'; // Rojo - no realizable
      if (isSegment) {
        fillColor = isPending ? '#9333ea' : '#16a34a'; // Púrpura pendiente, Verde validado
      }

      let polyline = null;
      if (isSpiderfied && clusterCenter) {
        polyline = L.polyline([clusterCenter, [displayLat, displayLng]], {
          color: '#3b82f6', weight: 2, opacity: 0.6, dashArray: '4, 4'
        }).addTo(map);
      }

      const marker = L.circleMarker([displayLat, displayLng], {
        radius: 12, fillColor: fillColor,
        color: '#fff', weight: 2, opacity: 1, fillOpacity: 0.85
      }).addTo(map);

      // Bloquear el clic del marcador para que no llegue al mapa y cierre la espiral
      marker.on('click', (e) => L.DomEvent.stopPropagation(e));

      let statusText = isSegment ? (isPending ? '⏳ Pendiente' : '✅ Validado') : '✗ No realizable';
      marker.bindPopup(`<b>Punto #${pt.id || '?'}</b><br/>${statusText}`);

      if (isPending) {
        marker.on('click', () => {
          const seg = segments.find(s => s.id === pt.id);
          if (seg) { setSelectedSegment(seg); setView('validate'); }
        });
      }

      return { marker, polyline };
    };

    // 3. Pintar los clústeres / puntos individuales
    clusters.forEach(clusterData => {
      const { lat, lng, points } = clusterData;

      if (points.length === 1) {
        renderOriginalPoint(points[0], lat, lng, false, null);
      } else {
        // Mostrar nodo "Agrupador / Clúster" tipo Google Earth
        const icon = L.divIcon({
          html: `<div style="background-color: #f59e0b; color: white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); font-size: 14px;">${points.length}</div>`,
          className: '',
          iconAnchor: [16, 16]
        });
        const clusterMarker = L.marker([lat, lng], { icon }).addTo(map);

        clusterMarker.on('click', (e) => {
          L.DomEvent.stopPropagation(e); // Prevenir el map.click
          clearSpiders(); // Si hay otra espiral abierta, la cerramos

          points.forEach((pt, idx) => {
            const angle = idx * (Math.PI * 2 / points.length);
            const radius = 0.00035; // Aprox 38m de separación
            // Ajustar visualmente según longitud
            const displayLat = lat + Math.sin(angle) * radius;
            const displayLng = lng + Math.cos(angle) * (radius / Math.cos(lat * Math.PI / 180));

            const res = renderOriginalPoint(pt, displayLat, displayLng, true, [lat, lng]);
            activeSpiders.push(res.marker);
            if (res.polyline) activeSpiders.push(res.polyline);
          });
        });
      }
    });

    if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] });
    leafletMapRef.current = map;

    // GPS del inspector en tiempo real
    if (navigator.geolocation) {
      const gpsIcon = L.divIcon({
        html: '<div style="width:18px;height:18px;background:#2563eb;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 3px rgba(37,99,235,0.35)"></div>',
        className: '', iconAnchor: [9, 9]
      });
      const updateGPS = (pos) => {
        if (!leafletMapRef.current) return;
        const latlng = [pos.coords.latitude, pos.coords.longitude];
        if (gpsMarkerRef.current) {
          gpsMarkerRef.current.setLatLng(latlng);
        } else {
          gpsMarkerRef.current = L.marker(latlng, { icon: gpsIcon, zIndexOffset: 1000 })
            .bindPopup('📍 Tu ubicación')
            .addTo(leafletMapRef.current);
        }
      };
      navigator.geolocation.getCurrentPosition(updateGPS, () => { }, { enableHighAccuracy: true });
      gpsWatchRef.current = navigator.geolocation.watchPosition(updateGPS, () => { }, {
        enableHighAccuracy: true, maximumAge: 15000, timeout: 20000
      });
    }
  };


  const handleValidationSuccess = () => {
    const validatedId = selectedSegment?.id;
    destroyMap();
    setView('list');
    setSelectedSegment(null);
    if (validatedId) {
      setSegments(prev => {
        const updated = prev.filter(s => s.id !== validatedId);
        localStorage.setItem(`inspector_cached_segments_${report.id}`, JSON.stringify(updated));
        return updated;
      });
    }
    if (navigator.onLine) {
      loadSegments();
    }
  };

  const realizables = segments.filter(s => s.type === 'realizable');
  const noRealizables = segments.filter(s => s.type === 'unrealizable');

  return (
    <div className="rd-overlay">
      <div className="rd-sheet">
        <div className="rd-header">
          <div className="rd-header-info">
            <h2 className="rd-title">{report.nombre}</h2>
            <p className="rd-sub">FFM: {report.ruta_id}</p>
          </div>
          <button className="rd-close" onClick={() => { destroyMap(); onClose(); }}>✕</button>
        </div>

        {error && <div className="val-error">{error}</div>}

        {view === 'list' && (
          <div className="rd-body">
            <div className="rd-stats-row">
              <div className="rd-stat green">
                <span className="rd-stat-num">{realizables.length}</span>
                <span className="rd-stat-lbl">por validar</span>
              </div>
              <div className="rd-stat red">
                <span className="rd-stat-num">{noRealizables.length}</span>
                <span className="rd-stat-lbl">no realizables</span>
              </div>
            </div>

            {loading ? (
              <div className="rd-loading">Cargando segmentos…</div>
            ) : realizables.length === 0 ? (
              <div className="rd-done">✅ Todos los segmentos validados</div>
            ) : (
              <>
                <p className="rd-hint">Toca un segmento para validarlo</p>
                <div className="rd-segs">
                  {realizables.map((seg, i) => (
                    <button key={seg.id} className="rd-seg-btn"
                      onClick={() => { setSelectedSegment(seg); setView('validate'); }}>
                      <span className="rd-seg-dot">{i + 1}</span>
                      <div className="rd-seg-info">
                        <span className="rd-seg-id">Segmento #{seg.id}</span>
                        {seg.submitted_at && (
                          <span className="rd-seg-time">
                            {new Date(seg.submitted_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <span className="rd-seg-arrow">›</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="rd-footer">
              <button onClick={loadMapData} disabled={loading} className="btn-map">
                {loading ? 'Cargando…' : '🗺️ Ver mapa'}
              </button>
              <button onClick={() => { destroyMap(); onClose(); }} className="btn-back">
                Cerrar
              </button>
            </div>
          </div>
        )}

        {view === 'map' && (
          <div className="rd-map-wrap">
            <div ref={mapRef} className="rd-map" />
            <p className="rd-map-hint">🟣 Toca un punto púrpura para validarlo (Verde = Validado)</p>
            <button onClick={handleCloseMap} className="btn-back rd-map-back">
              ← Volver a lista
            </button>
          </div>
        )}

        {view === 'validate' && selectedSegment && (
          <SegmentValidationModal
            segment={selectedSegment}
            report={report}
            onSuccess={handleValidationSuccess}
            onCancel={handleCancelValidation}
            onOfflineQueued={onOfflineQueued}
          />
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// Dashboard principal PI
// ─────────────────────────────────────────────
export default function InspectorDashboardTab() {
  // Restaurar selección de distrito/semana desde sessionStorage para no perder contexto al cambiar tabs
  const [selectedDistrict, setSelectedDistrictState] = useState(
    () => sessionStorage.getItem('pi_selected_district') || ''
  );
  const [selectedWeek, setSelectedWeekState] = useState(
    () => sessionStorage.getItem('pi_selected_week') || ''
  );
  const [reports, setReports] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [districts, setDistricts] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [offlineToast, setOfflineToast] = useState(null);
  const allowedWeeks = React.useMemo(() => getAllowedWeekIds(), []);

  // Wrappers que persisten en sessionStorage
  const setSelectedDistrict = (val) => {
    setSelectedDistrictState(val);
    if (val) sessionStorage.setItem('pi_selected_district', val);
    else sessionStorage.removeItem('pi_selected_district');
  };
  const setSelectedWeek = (val) => {
    setSelectedWeekState(val);
    if (val) sessionStorage.setItem('pi_selected_week', val);
    else sessionStorage.removeItem('pi_selected_week');
  };

  // ── Auto-sync de validaciones PI guardadas offline ──────────────────
  const refreshReportsTimer = useRef(null);
  const offlineSync = useOfflineSync({
    onActionSynced: (action) => {
      if (action.type === 'validate_pi_segment') {
        setOfflineToast(`✅ Validación PI enviada: ${action.label}`);
        // Recargar recorridos con debounce para no saturar si se envían varias acciones juntas
        if (refreshReportsTimer.current) clearTimeout(refreshReportsTimer.current);
        refreshReportsTimer.current = setTimeout(() => {
          loadReports();
        }, 1500);
        setTimeout(() => setOfflineToast(null), 4000);
      }
    }
  });
  const { isOnline, pendingActions: piPendingActions, isSyncing: piSyncing } = offlineSync;

  const handleCloseReportDetails = useBackHandler(showDetails, () => {
    setShowDetails(false);
    setSelectedReport(null);
    loadReports();
  }, 'reportDetails');

  useEffect(() => { loadDistricts(); }, []); // eslint-disable-line

  // Restaurar semanas cuando el distrito ya estaba guardado en sessionStorage
  useEffect(() => {
    if (selectedDistrict) loadWeeks(selectedDistrict);
  }, []); // eslint-disable-line

  const loadDistricts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/inspector/districts`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Error cargando distritos');
      const data = await res.json();
      setDistricts(data);
      localStorage.setItem('inspector_cached_districts', JSON.stringify(data));
    } catch (err) {
      console.warn('⚠️ Sin conexión al cargar distritos, usando caché local...');
      const cached = localStorage.getItem('inspector_cached_districts');
      if (cached) {
        try { setDistricts(JSON.parse(cached) || []); } catch (e) {}
      } else {
        setError(err.message);
      }
    }
  };

  const loadWeeks = async (districtId) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/inspector/districts/weeks?district=${encodeURIComponent(districtId)}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Error cargando semanas');
      const data = await res.json();
      const filtered = data.filter(w => allowedWeeks.includes(w.id));
      setWeeks(filtered);
      localStorage.setItem(`inspector_cached_weeks_${districtId}`, JSON.stringify(data));
    } catch (err) {
      console.warn('⚠️ Sin conexión al cargar semanas, usando caché local...');
      const cached = localStorage.getItem(`inspector_cached_weeks_${districtId}`);
      if (cached) {
        try {
          const data = JSON.parse(cached);
          setWeeks(data.filter(w => allowedWeeks.includes(w.id)));
        } catch (e) {}
      } else {
        setError(err.message);
      }
    }
  };

  const loadReports = useCallback(async () => {
    if (!selectedDistrict || !selectedWeek) return;
    try {
      setLoading(true);
      const res = await fetchWithRetry(
        `${API_BASE_URL}/inspector/reports?district=${encodeURIComponent(selectedDistrict)}&week=${encodeURIComponent(selectedWeek)}`,
        { headers: getAuthHeaders() },
        2, 1000, 10000
      );
      if (!res.ok) throw new Error('Error cargando recorridos');
      const data = await res.json();
      setReports(data);
      localStorage.setItem(`inspector_cached_reports_${selectedDistrict}_${selectedWeek}`, JSON.stringify(data));
    } catch (err) {
      console.warn('⚠️ Sin conexión al cargar recorridos, usando caché local...');
      const cached = localStorage.getItem(`inspector_cached_reports_${selectedDistrict}_${selectedWeek}`);
      if (cached) {
        try { setReports(JSON.parse(cached) || []); } catch (e) {}
      } else {
        const isNet = !navigator.onLine || err.name === 'TypeError' || err.message?.toLowerCase().includes('fetch');
        if (!isNet) setError(err.message);
      }
    }
    finally { setLoading(false); }
  }, [selectedDistrict, selectedWeek]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const handleDistrictChange = (e) => {
    const d = e.target.value;
    setSelectedDistrict(d);
    setSelectedWeek('');
    setReports([]);
    setWeeks([]);
    if (d) loadWeeks(d);
  };

  // Callback que recibe el SegmentValidationModal cuando guarda offline
  const handleOfflineQueued = (msg) => {
    setOfflineToast(msg);
    offlineSync.refreshPendingCount();
    setTimeout(() => setOfflineToast(null), 5000);
  };

  return (
    <div className="pi-wrap">
      <div className="pi-hero">
        <h1 className="pi-hero-title">Planta Interna</h1>
        <p className="pi-hero-sub">Validación de recorridos en campo</p>
      </div>

      {/* ── Banner de estado offline / pendientes ── */}
      {!isOnline && (
        <div style={{
          background: 'linear-gradient(135deg, #ea580c, #f97316)',
          color: '#fff', padding: '10px 16px', borderRadius: 10, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(234,88,12,0.3)'
        }}>
          <span>📴</span>
          <span>Sin señal — las validaciones se guardarán automáticamente y se enviarán cuando recuperes conexión.</span>
        </div>
      )}
      {isOnline && piPendingActions > 0 && (
        <div style={{
          background: piSyncing
            ? 'linear-gradient(135deg, #2563eb, #3b82f6)'
            : 'linear-gradient(135deg, #059669, #10b981)',
          color: '#fff', padding: '10px 16px', borderRadius: 10, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600,
          boxShadow: piSyncing ? '0 4px 12px rgba(37,99,235,0.3)' : '0 4px 12px rgba(5,150,105,0.3)'
        }}>
          <span>{piSyncing ? '🔄' : '📶'}</span>
          <span>
            {piSyncing
              ? `Enviando ${piPendingActions} validación(es) offline al servidor...`
              : `${piPendingActions} validación(es) PI pendientes de envío. Se sincronizarán automáticamente.`
            }
          </span>
          {!piSyncing && (
            <button
              onClick={() => offlineSync.syncNow()}
              style={{ marginLeft: 'auto', background: '#fff', color: '#047857', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
            >
              Enviar ahora
            </button>
          )}
        </div>
      )}
      {/* ── Reporte de diagnóstico del último envío ── */}
      {offlineSync.lastReport && (() => {
        const r = offlineSync.lastReport;
        const palette = {
          ok:    { bg: '#f0fdf4', border: '#86efac', fg: '#166534' },
          info:  { bg: '#eff6ff', border: '#93c5fd', fg: '#1e40af' },
          warn:  { bg: '#fff7ed', border: '#fdba74', fg: '#9a3412' },
          error: { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b' }
        };
        const c = palette[r.level] || palette.info;
        const icon = { ok: '✅', info: 'ℹ️', warn: '⚠️', error: '❌' };
        return (
          <div style={{
            background: c.bg, border: `1.5px solid ${c.border}`, color: c.fg,
            padding: '12px 14px', borderRadius: 10, marginBottom: 12, fontSize: 13
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <strong style={{ flex: 1 }}>{r.title}</strong>
              <button
                onClick={offlineSync.clearReport}
                aria-label="Cerrar reporte"
                style={{ background: 'transparent', border: 'none', color: c.fg, fontSize: 16, cursor: 'pointer', lineHeight: 1 }}
              >✕</button>
            </div>
            <ul style={{ margin: '8px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {r.lines.map((l, i) => (
                <li key={i} style={{ fontWeight: 500, lineHeight: 1.35 }}>{icon[l.level] || '•'} {l.text}</li>
              ))}
            </ul>
            <div style={{ marginTop: 8, fontSize: 11, opacity: 0.7 }}>
              {new Date(r.at).toLocaleString()}
            </div>
          </div>
        );
      })()}
      {/* Toast de acción offline guardada / sincronizada */}
      {offlineToast && (
        <div style={{
          background: offlineToast.startsWith('✅') ? '#f0fdf4' : '#fff7ed',
          border: `1.5px solid ${offlineToast.startsWith('✅') ? '#86efac' : '#fdba74'}`,
          color: offlineToast.startsWith('✅') ? '#15803d' : '#c2410c',
          padding: '10px 16px', borderRadius: 10, marginBottom: 12,
          fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span>{offlineToast}</span>
        </div>
      )}

      {error && <div className="pi-error">{error}</div>}

      <div className="pi-card">
        <label className="pi-lbl">Distrito</label>
        <select className="pi-select" value={selectedDistrict} onChange={handleDistrictChange}>
          <option value="">— Seleccionar —</option>
          {districts.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>

        {selectedDistrict && weeks.length > 0 && (
          <>
            <label className="pi-lbl mt-12">Semana</label>
            <div className="pi-week-row">
              {weeks.map(w => (
                <button key={w.id}
                  className={`pi-week-btn ${selectedWeek === w.id ? 'active' : ''}`}
                  onClick={() => { setSelectedWeek(w.id); setReports([]); }}>
                  <span className="pi-week-num">Semana {w.number}</span>
                  <span className="pi-week-tag">
                    {w.id === allowedWeeks[0] ? 'actual' :
                      w.id === allowedWeeks[1] ? 'anterior' : 'hace 2 semanas'}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {selectedDistrict && weeks.length === 0 && (
          <p className="pi-no-weeks">Sin recorridos en las últimas 3 semanas</p>
        )}
      </div>

      {selectedDistrict && selectedWeek && (
        <div className="pi-section">
          <h3 className="pi-section-title">Recorridos disponibles</h3>
          {loading ? (
            <div className="pi-spinner">Cargando…</div>
          ) : reports.length === 0 ? (
            <div className="pi-empty">No hay recorridos en esta semana</div>
          ) : (
            <div className="pi-grid">
              {(() => {
                const isPeEnabled = (process.env.REACT_APP_ENV === 'staging' || process.env.REACT_APP_ENABLE_PE_VERIFICATIONS === 'true');
                const visibleReports = isPeEnabled ? reports : reports.filter(r => r.tipo !== 'PE');
                if (visibleReports.length === 0) {
                  return <div className="pi-empty">No hay recorridos de Planta Interna en esta semana</div>;
                }
                return visibleReports.map(r => {
                  const esPE = r.tipo === 'PE';
                  return (
                    <button key={`${r.tipo || 'PI'}-${r.id}`}
                      className={`pi-report-card ${r.pending_segments === 0 ? 'done' : ''}`}
                      style={{ position: 'relative', overflow: 'hidden', paddingTop: '36px', textAlign: 'left' }}
                      onClick={() => { setSelectedReport(r); setShowDetails(true); }}>
                      {/* ETIQUETA SUPERIOR MUY LLAMATIVA */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: esPE ? '#059669' : '#2563eb',
                        color: '#ffffff',
                        fontWeight: '800',
                        fontSize: '0.78rem',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        padding: '5px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <span>{esPE ? '⚡ PLANTA EXTERNA (PE)' : '🏢 PLANTA INTERNA (PI)'}</span>
                        <span style={{ fontSize: '0.7rem', opacity: 0.9 }}>{esPE ? 'HALLAZGO' : 'RECORRIDO'}</span>
                      </div>
                      <div className="pi-rc-name" style={{ marginTop: '4px' }}>{r.nombre}</div>
                      <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '8px' }}>
                        <strong>{esPE ? 'OT / Hallazgo:' : 'ID Recorrido:'}</strong> {esPE ? (r.ot || r.id_hallazgo || r.id) : r.id}
                      </div>
                      <div className="pi-rc-foot">
                        <span className="pi-rc-ffm">{esPE ? 'Técnico/FFM:' : 'FFM:'} {r.ruta_id}</span>
                        {r.pending_segments === 0
                          ? <span className="pi-badge-done">✓ Completo</span>
                          : <span className="pi-badge-pend">{r.pending_segments} pendientes</span>}
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      {showDetails && selectedReport && (
        <ReportDetailsModal
          report={selectedReport}
          onClose={handleCloseReportDetails}
          onOfflineQueued={handleOfflineQueued}
        />
      )}
    </div>
  );
}
